import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  Container,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  Fade,
  Collapse,
} from '@mui/material';

import Breadcrumbs, {
  BreadcrumbOption,
  getBreadCrumbOptionsByTopicAndSubTopicData,
} from '../../../components/BreadCrumb/Breadcrumbs';
import {
  ISubmission,
  createNewSubmissionByQuizId,
  getSubmissionState,
  getSubmissionsForQuizHistory,
  loadMoreSubmissionsForQuizHistory,
  resetSubmissionListData,
} from '../../../store/SubmissionSlice';
import {
  ISubTopic,
  getSubTopicById,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import { ITopic } from '../../../store/TopicSlice';
import { IQuiz, getQuizById, getQuizState } from '../../../store/QuizSlice';
import { useAppSelector } from '../../../hooks';
import { getUrlConvertedString } from '../../../helper';
import { TP_COLORS } from '../../../constants';
import QuizListItem from './components/QuizListItem';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import SubmissionListItem from './components/SubmissionListItem';

const constructBreadcrumbOptions = (
  topicData?: ITopic,
  subTopicData?: ISubTopic
) => {
  const options: Array<BreadcrumbOption> = getBreadCrumbOptionsByTopicAndSubTopicData(
    topicData,
    subTopicData
  );

  if (topicData && subTopicData) {
    const urlConvertedCurriculum = getUrlConvertedString(topicData.curriculum);
    const urlConvertedSubject = getUrlConvertedString(topicData.subject);
    const urlConvertedTopic = getUrlConvertedString(topicData.name);
    const urlConvertedSubTopic = getUrlConvertedString(subTopicData.name);

    options.push({
      type: 'link',
      title: 'Quiz',
      link: `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicData._id}/quiz`,
    });
  }

  options.push({
    type: 'text',
    title: 'Quiz History',
    highlight: true,
  });

  return options;
};

const containerStyles: SxProps = {
  background: `linear-gradient(180deg, #01736D 0%, #001615 100%)`,
  minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
};

const submissionListContainerStyles: React.CSSProperties = {
  backgroundColor: '#F8FAFC',
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  border: '1px solid #E2E8F0',
  borderBottomLeftRadius: '5px',
  borderBottomRightRadius: '5px',
};

const initialSubmissionFilterData = {
  limit: 30,
  offset: 0,
};

const AboutQuizHistoryPage: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Store
  const quizState = useAppSelector(getQuizState);
  const submissionState = useAppSelector(getSubmissionState);
  const subTopicState = useAppSelector(getSubTopicState);

  const quizData = quizState.quiz;
  const topicData = quizData && quizData.topic ? quizData.topic : undefined;
  const subTopicId = params.subtopicId;
  const subTopicData = subTopicState.subTopic;
  const breadcrumbOptions = constructBreadcrumbOptions(topicData, subTopicData);

  const urlConvertedCurriculum = getUrlConvertedString(
    topicData?.curriculum || ''
  );

  const urlConvertedSubject = getUrlConvertedString(topicData?.subject || '');

  const urlConvertedTopic = getUrlConvertedString(topicData?.name || '');

  // State
  const [submissionFilterData, setSubmissionFilterData] = useState(
    initialSubmissionFilterData
  );

  useEffect(() => {
    dispatch(resetSubmissionListData());

    if (params.quizId) {
      dispatch(getQuizById(params.quizId));
      dispatch(
        getSubmissionsForQuizHistory({
          ...submissionFilterData,
          quizId: params.quizId,
        })
      );
    }

    if (subTopicId) {
      dispatch(getSubTopicById(subTopicId));
    }
  }, [params.quizId]);

  const handleSubmissionLoadMore = () => {
    setSubmissionFilterData((prevState) => {
      const newSubmissionFilterData = {
        ...prevState,
        offset: submissionState.list.length,
      };

      if (params.quizId) {
        dispatch(
          loadMoreSubmissionsForQuizHistory({
            ...submissionFilterData,
            quizId: params.quizId,
          })
        );
      }

      return newSubmissionFilterData;
    });
  };

  const onTakeQuiz = (quiz: IQuiz) => {
    if (subTopicId) {
      dispatch(
        createNewSubmissionByQuizId({
          subTopicId,
          quizId: quiz._id,
        })
      );
    }
  };

  const onViewSubmission = (submission: ISubmission) => {
    const urlConvertedSubTopic = getUrlConvertedString(
      subTopicData?.name || ''
    );

    if (submission.status === 'pending') {
      navigate(
        `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}/quiz/${submission._id}`
      );
    } else {
      navigate(
        `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}/view-quiz/${submission._id}`
      );
    }
  };

  return (
    <Box sx={containerStyles}>
      <Container maxWidth="lg">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs options={breadcrumbOptions} showDropDown={belowMd} />
        </div>
        <div style={{ paddingBottom: '15px' }}>
          <Typography fontWeight={500} color={TP_COLORS.white} fontSize="25px">
            Quiz History
          </Typography>
        </div>
        <div style={{ paddingBottom: '15px' }}>
          {quizData && topicData && subTopicData ? (
            <Fade in>
              <Card style={{ borderRadius: '30px' }}>
                <CardContent style={{ padding: '0' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <QuizListItem
                        matchBotoomStyles
                        onTakeQuiz={() => onTakeQuiz(quizData)}
                        quiz={{
                          ...quizData,
                          submissionCount: submissionState.count || 0,
                        }}
                      />
                      <Card elevation={0} style={submissionListContainerStyles}>
                        <CardContent style={{ padding: '32px 16px' }}>
                          <Box>
                            <Stack>
                              {submissionState.status === 'loading' &&
                              !(submissionState.list || []).length ? (
                                <div style={{ padding: '30px 0' }}>
                                  <BasicLoader />
                                </div>
                              ) : null}
                              <Collapse
                                in={
                                  (submissionState.list || []).length > 0 ||
                                  submissionState.status !== 'loading'
                                }
                              >
                                <Grid container spacing={2}>
                                  {(submissionState.list || []).map(
                                    (item, index) => {
                                      const key = `${quizData._id}-submission-li-${index}`;
                                      const attemptNumber = (index + 1)
                                        .toString()
                                        .padStart(2, '0');

                                      return (
                                        <Grid key={key} item xs={12}>
                                          <SubmissionListItem
                                            submission={item}
                                            attemptNumber={attemptNumber}
                                            onView={() =>
                                              onViewSubmission(item)
                                            }
                                          />
                                        </Grid>
                                      );
                                    }
                                  )}
                                  {submissionState.count >
                                  submissionState.list.length ? (
                                    <Grid item xs={12}>
                                      <Stack
                                        direction="row"
                                        justifyContent="center"
                                      >
                                        <LoadingButton
                                          sx={{ color: '#01A79F' }}
                                          onClick={handleSubmissionLoadMore}
                                          loading={
                                            submissionState.status === 'loading'
                                          }
                                        >
                                          Load More
                                        </LoadingButton>
                                      </Stack>
                                    </Grid>
                                  ) : null}
                                  {submissionState.status !== 'loading' &&
                                  (submissionState.list || []).length === 0 ? (
                                    <Grid item xs={12}>
                                      <Typography
                                        fontWeight={600}
                                        textAlign="center"
                                      >
                                        It seems you haven&apos;t done any
                                        quizzes. Please take a quiz.
                                      </Typography>
                                    </Grid>
                                  ) : null}
                                </Grid>
                              </Collapse>
                            </Stack>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Fade>
          ) : null}
        </div>
      </Container>
    </Box>
  );
};

export default AboutQuizHistoryPage;
